import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import Widget from './Widget';
import { orderBy } from 'lodash';
import { Row, Col } from '../ui-kit';
import { isMobileOnly, isDesktop } from 'react-device-detect';
import IncidentSelector from './widgets/shared/IncidentWidgets/IncidentSelector';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Dashboard.css';

const DashboardPageResponsive = props => {
  const ResponsiveGridLayout = useMemo(() => WidthProvider(Responsive), []);

  const [draggable, setDraggable] = useState(true);

  useEffect(() => {
    // When the displayable incidents change, we need to decide whether to unlock the dashboard
    // active incident, or keep it locked.
    // If the incident that was locked is no longer displayed, we unlock it.
    // If the incident that was locked is still displayed, we keep it locked.
    const selectedDisplayed = props.displayableIncidents.map(i => i.id).includes(props.currentDisplayedIncidentId);

    if (props.incidentLocked && !selectedDisplayed) {
      toggleIncidentLock();
      // We fall back to the first displayable incident if there's any
      // other incident active
      if (props.displayableIncidents.length > 0) {
        props.setDisplayedIncidentId(props.displayableIncidents[0].id);
      }
    }
  }, [props.displayableIncidents, props.currentDisplayedIncidentId]);

  const orderedWidgets = useMemo(() => {
    if (isDesktop) return props.activePage.widgets;

    return orderBy(props.activePage.widgets, [widget => (widget.position_y + 1) * 100 + widget.position_x], ['asc']);
  }, [props.activePage.widgets]);

  // update the widget geometry based on the new layout
  const onLayoutChange = currentLayout => {
    if (!props.editMode) return;

    const layout = currentLayout;
    // update the geometry of widgets on the active page
    const newWidgets = layout.map(block => {
      const widget = props.activePage.widgets.find(w => String(block.i) === String(w.id));

      if (!widget) return {};

      if (
        widget.position_x !== block.x ||
        widget.position_y !== block.y ||
        widget.height !== block.h ||
        widget.width !== block.w
      ) {
        props.markAsChanged();
      }

      return {
        ...widget,
        position_x: block.x,
        position_y: block.y,
        height: block.h,
        width: block.w
      };
    });

    props.updateWidgets(newWidgets, props.activePageIndex);
  };

  const removeWidget = useCallback(
    widgetId => {
      props.markAsChanged();
      props.removeWidget(widgetId);
    },
    [props.activePage?.id]
  );

  const toggleIncidentLock = useCallback(() => props.lockIncident(!props.incidentLocked), [props.incidentLocked]);

  if (!props.columns) return null;

  const renderGrid = isSingleWidgetMode => {
    if (isSingleWidgetMode) return renderWidgets(isSingleWidgetMode);
    // Note: the classname and draggableHandle are manipulated instead
    // of setting isDraggable and isResizable on the GridLayout. See
    // Dashboard.css for an explanation why
    //
    // Note the use of draggableCancel here. When a modal is open (e.g. a
    // component with .modal-open class exists) dragging is blocked. This is to
    // prevent that dragging in a modal would also move the widgets in the
    // dashboard under the modal
    return (
      <ResponsiveGridLayout
        className={`layout ${props.editMode ? 'editable' : ''}`}
        layouts={layouts}
        breakpoints={{ lg: 992, sm: 576 }}
        cols={{ lg: props.columns || 6, sm: 1 }}
        rowHeight={30}
        autoSize={true}
        onLayoutChange={onLayoutChange}
        draggableHandle={props.editMode && draggable ? null : 'none'}
        draggableCancel={'.modal-open'}
      >
        {renderWidgets(isSingleWidgetMode)}
      </ResponsiveGridLayout>
    );
  };

  const renderWidgets = isSingleWidgetMode =>
    // the outer div will become the react-grid-item and is required
    orderedWidgets.map(widget => (
      <div key={widget.id} className={isMobileOnly ? 'mb-2' : null}>
        <Widget
          widgetArchetype={props.widgetArchetypes[widget.archetype_id]}
          editMode={props.editMode}
          singleWidgetMode={isSingleWidgetMode}
          // pass in the group id of the dashboard, which can be
          // used as fallback for many widgets that operate in the
          // scope of a group
          dashboardGroupId={props.dashboard.group_id}
          dashboardAccessScopeGroupId={props.dashboard.access_scope_group_id}
          widgetData={widget}
          removeWidget={removeWidget}
          saveDashboard={props.saveDashboard}
          updateWidgetSettings={props.updateWidgetSettings}
          updateWidgetTitle={props.updateWidgetTitle}
          updateWidgetHeight={props.updateWidgetHeight}
          enableDragging={setDraggable}
          setDisplayedIncident={props.setDisplayedIncidentId}
          locked={props.incidentLocked}
          toggleLock={toggleIncidentLock}
        />
      </div>
    ));

  const layouts = { lg: props.activePageWidgetCoordinates };
  const isSingleWidgetMode = props.singleWidgetMode;
  // When building a normal dashboard an incident page is added automatically,
  // we want to the change to incident mode only on normal dashboards.
  const shouldRotateIncidents = props.displayableIncidents.length > 1;

  if (isMobileOnly) {
    return renderWidgets(isSingleWidgetMode);
  }

  return (
    <>
      <Row className="dashboard-content">
        <Col lg={12}>{renderGrid(isSingleWidgetMode)}</Col>
      </Row>
      {shouldRotateIncidents && (
        <IncidentSelector
          displayedIncident={props.currentDisplayedIncident}
          setDisplayedIncidentId={props.setDisplayedIncidentId}
          locked={props.incidentLocked}
          toggleLock={toggleIncidentLock}
          displayableIncidents={props.displayableIncidents}
          displayNextIncident={props.displayNextIncident}
        />
      )}
    </>
  );
};

export default DashboardPageResponsive;
